import { useState, type FormEvent } from "react";
import { UserPlus } from "@phosphor-icons/react";
import { usePortalIdentity } from "../auth/usePortalIdentity";
import { STEP_UP_FAILURE_TEXT, useVerifiedFactor } from "../auth/stepUp";
import { useStepUpProof, type StepUpFactorState } from "../auth/useStepUpProof";
import {
  useMembershipRequests,
  useRequestMembershipChange,
  useTenantPeople,
  type MembershipRequest,
  type MembershipRole,
  type TenantPerson
} from "../data/membershipQueries";
import { BoxState } from "../ui/BoxState";
import { Panel } from "../ui/Panel";
import { Sheet } from "../ui/Sheet";
import "./clientSettings/settings.css";
import { useViewOnly } from "../data/readSource";
import { PersonalLeftOut } from "./asClient/PersonalLeftOut";

/**
 * Åtkomst: who at the client has a login, and the authorised contact asking Petrovic to change that.
 *
 * plan.md section 4 gives the right to one role and states the constraint the whole panel is built
 * around: "There is no public registration. Petrovic creates or approves every membership." So this
 * screen asks and never grants. `public.request_membership_change` writes a row into
 * `private.membership_invitations`, Petrovic approves it from `app/routes/AdminOperationsRoute.tsx`,
 * and the operations tick sends the person an invitation within a minute. Nobody, the person asking
 * included, ever holds the new person's password: GoTrue mails a single-use link and the person
 * chooses it themselves.
 *
 * **A new colleague does not have to be a contact first.** Until 2026-09-13 the form was a picker
 * over existing contacts, because `membership_invitations.contact_id` was `not null`, and the panel
 * told a client to ask in the chat for anybody else. That was the one case the panel exists for. The
 * request now carries a name and an address instead, and Petrovic's approval is what records the
 * person as a contact.
 *
 * **A client member sees the list and not the form.** plan.md section 4: a client member "cannot end
 * management or change access". The gateway raises `PORTAL_FORBIDDEN_ROLE` for anybody but the
 * authorised contact, and `tests/db/` proves it there; this is only what the interface offers.
 *
 * **The step-up is required and is not this screen's decision.** plan.md section 8 lists "access
 * changes" among the actions needing recent re-authentication, and `public.request_membership_change`
 * consumes a grant in the same transaction as the insert. `app/auth/useStepUpProof.tsx` asks for the
 * factor or the password, whichever the account actually has.
 *
 * **Personer med åtkomst, since the second build round of 2026-09-24** (`prototype/kund/pages.js`,
 * the `personer` section): one box with a row per person and the request in its foot, the request
 * form in a sheet, and the requests already made in a second box. The lead under the old heading went
 * under design.md section 3's third rule.
 */

const dateFormat = new Intl.DateTimeFormat("sv-SE", { dateStyle: "long" });

function formatMoment(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : dateFormat.format(parsed);
}

const ROLE_LABEL: Record<MembershipRole, string> = {
  client_authorised_contact: "Behörig kontaktperson",
  client_member: "Användare"
};

/**
 * What the two roles actually mean, in the words plan.md section 4 uses for them.
 *
 * Written out beside the choice rather than left to the label, because the difference between them
 * is the whole of what is being asked for and the two names do not carry it on their own.
 */
const ROLE_EXPLANATION: Record<MembershipRole, string> = {
  client_authorised_contact:
    "Kan allt en användare kan, och dessutom säga upp eller återuppta förvaltningen, ändra era notisinställningar och be om ändringar här.",
  client_member:
    "Ser era ärenden, chatten, statistiken och era dokument. Kan inte säga upp förvaltningen och inte ändra vilka som har åtkomst."
};

/** The picker's value for "somebody who is not in the list yet". Not a UUID, so it cannot collide. */
const NEW_PERSON = "ny-person";

/* ------------------------------------------------------------ who has what */

/**
 * A membership's status, in the words Petrovic's own Personer uses for the same row
 * (`app/routes/adminClient/PeopleVocabulary.ts`), restated rather than imported because the client's
 * screens do not import from the admin's. Until 2026-09-24 the raw value, `suspended` or `disabled`,
 * was printed in English beside the role. A status this build does not know is left out rather than
 * printed as a database word.
 */
const STATUS_LABEL: Record<string, string | undefined> = {
  invited: "Inbjuden",
  suspended: "Spärrat",
  disabled: "Avstängt"
};

function roleLine(person: TenantPerson): string {
  /*
    Three states and not two. Somebody with no membership row has no login at all, which is an
    ordinary state for a contact who signs papers and never opens the portal, and it must not read as
    "access removed". A membership that is not active says which state it is in, because `suspended`
    and `disabled` are things Petrovic did and the client should be able to see that they happened.
  */
  if (person.currentRole === null) {
    return "Ingen inloggning";
  }

  const role = ROLE_LABEL[person.currentRole];
  const status = person.membershipStatus === "active" ? undefined : STATUS_LABEL[person.membershipStatus ?? ""];
  return status ? `${role} · ${status}` : role;
}

function AccessList({ people }: { people: TenantPerson[] }) {
  if (people.length === 0) {
    return (
      <BoxState
        kind="empty"
        title="Inga personer registrerade ännu"
        body="Be om åtkomst för en ny person nedan, så lägger Petrovic Systems upp kontot."
        compact
      />
    );
  }

  return (
    <ul className="k-rows" data-testid="access-list">
      {people.map((person) => (
        <li key={person.contactId} className="k-item k-person" data-testid="access-row">
          <span className="primary-cell k-row-main">
            <strong>{person.fullName}</strong>
            <span>{[person.jobTitle, person.email].filter(Boolean).join(" · ")}</span>
          </span>
          <span className="quiet k-row-trail" data-testid="access-role">
            {roleLine(person)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ----------------------------------------------------------- what was asked */

function OutcomeLine({ request }: { request: MembershipRequest }) {
  const asked = formatMoment(request.requested_at);
  const expires = formatMoment(request.expires_at);

  const sentence =
    request.outcome === "open"
      ? expires
        ? `Väntar på svar. Förfrågan gäller till ${expires}.`
        : "Väntar på svar."
      : request.outcome === "approved"
        ? "Beviljad. Inbjudan skickas inom någon minut."
        : request.outcome === "granted"
          ? "Klar. Personen har fått en inbjudan per mejl och väljer sitt eget lösenord."
          : request.outcome === "answered"
            ? "Besvarad. Listan ovanför visar vilken åtkomst som gäller nu."
            : "Gick ut obesvarad. Fråga igen om det fortfarande är aktuellt.";

  return (
    <li className="k-item k-person" data-testid="membership-request">
      <span className="primary-cell k-row-main">
        <strong>{request.contact_name}</strong>
        <span>
          {ROLE_LABEL[request.role as MembershipRole] ?? request.role}
          {asked ? ` · begärd ${asked}` : ""}
        </span>
        <span data-testid="membership-outcome">{sentence}</span>
      </span>
    </li>
  );
}

/* -------------------------------------------------------------- the request */

interface FormProps {
  tenantId: string;
  people: TenantPerson[];
  factor: StepUpFactorState;
  onSent: () => void;
  onCancel: () => void;
}

/**
 * The request, in the sheet (the sketch's `andring-atkomst`). The sheet renders nothing while closed,
 * so every opening starts empty and a cancelled request leaves nothing typed behind.
 */
function RequestForm({ tenantId, people, factor, onSent, onCancel }: FormProps) {
  const [contactId, setContactId] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MembershipRole>("client_member");
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [proof, proofFields] = useStepUpProof({
    action: "membership_change",
    factor,
    disabled: pending,
    idPrefix: "membership",
    totpReason:
      "Vi behöver veta att det är du innan vi skickar en förfrågan om åtkomst. Koden är det som styrker det.",
    passwordReason:
      "Vi behöver veta att det är du innan vi skickar en förfrågan om åtkomst. Kontot har ingen verifieringsapp, så lösenordet är det som styrker det. Du loggas inte ut och lösenordet ändras inte."
  });

  const ask = useRequestMembershipChange();
  const isNew = contactId === NEW_PERSON;
  const chosen = people.find((person) => person.contactId === contactId) ?? null;
  const newPersonReady =
    isNew && fullName.trim().length > 0 && /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email.trim());
  const personReady = Boolean(chosen) || newPersonReady;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!personReady || !proof.ready || pending) {
      return;
    }

    setPending(true);
    setFailure(null);
    let sent = false;

    try {
      const grant = await proof.request(tenantId);

      if (!grant.ok) {
        // `action-not-wrapped` gets this panel's own sentence rather than the shared one. The shared
        // text in `app/auth/stepUp.ts` is written about a cancellation and tells the reader nothing
        // is uppsagt, which would be a true sentence about the wrong thing here.
        setFailure(
          grant.reason === "action-not-wrapped"
            ? "Portalen kan inte styrka en åtkomstförfrågan ännu. Ingenting har ändrats. Skriv i chatten så tar vi det den vägen."
            : STEP_UP_FAILURE_TEXT[grant.reason]
        );
        return;
      }

      await ask.mutateAsync({
        tenantId,
        contactId: chosen ? chosen.contactId : null,
        // Sent as typed. `public.request_membership_change` lowercases it in the same statement as
        // the insert, and a second normalisation here would be one that can drift.
        email: chosen ? chosen.email : email.trim(),
        fullName: chosen ? null : fullName.trim(),
        role,
        stepUpToken: grant.token
      });

      sent = true;
    } catch (error) {
      setFailure((error as { message?: string }).message ?? "Något gick fel. Försök igen.");
    } finally {
      // A Turnstile token is single use, so the next attempt needs a fresh one whatever happened.
      proof.reset();
      setPending(false);
    }

    // After the `finally`, so the form's own state is settled before the sheet that holds it closes.
    if (sent) {
      onSent();
    }
  }

  return (
    <form className="k-form member-request" onSubmit={onSubmit}>
      <div className="field">
        <label htmlFor="membership-person">Vem gäller det?</label>
        <select
          id="membership-person"
          value={contactId}
          disabled={pending}
          onChange={(event) => setContactId(event.target.value)}
        >
          <option value="">Välj person</option>
          <option value={NEW_PERSON}>En ny person som inte står i listan</option>
          {people.map((person) => (
            <option key={person.contactId} value={person.contactId}>
              {person.fullName}
            </option>
          ))}
        </select>
      </div>

      {isNew ? (
        <>
          <div className="field">
            <label htmlFor="membership-name">För- och efternamn</label>
            <input
              id="membership-name"
              autoComplete="off"
              maxLength={200}
              value={fullName}
              disabled={pending}
              onChange={(event) => setFullName(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="membership-email">E-postadress</label>
            <input
              id="membership-email"
              type="email"
              autoComplete="off"
              inputMode="email"
              value={email}
              disabled={pending}
              onChange={(event) => setEmail(event.target.value)}
            />
            <p className="settings-note">
              Inbjudan går hit. Personen väljer sitt eget lösenord, och ingen annan får veta det.
            </p>
          </div>
        </>
      ) : null}

      {/*
        A fieldset rather than a div with a heading, so the group has an accessible name and a
        screen reader announces "Vilken åtkomst?" before either option. The label wraps its own
        radio, which makes the whole row the target rather than the twenty pixels of the control.
      */}
      <fieldset className="k-choices">
        <legend>Vilken åtkomst?</legend>

        {(["client_member", "client_authorised_contact"] as MembershipRole[]).map((value) => (
          <label key={value} className="k-choice">
            <input
              type="radio"
              name="membership-role"
              value={value}
              checked={role === value}
              disabled={pending}
              onChange={() => setRole(value)}
            />
            <span className="primary-cell">
              <strong>{ROLE_LABEL[value]}</strong>
              <span>{ROLE_EXPLANATION[value]}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {/*
        Said before the step-up rather than after the request lands. A person choosing a role is
        deciding what somebody else will be able to do, and the fact that the portal is not the thing
        that grants it changes what they expect to happen next.
      */}
      <p className="settings-note">
        Förfrågan går till Petrovic Systems. Säger Petrovic Systems ja får personen en inbjudan per mejl och väljer
        sitt eget lösenord.
      </p>

      {proofFields}

      {failure ? (
        <p className="form-error" role="alert" data-testid="membership-error">
          {failure}
        </p>
      ) : null}

      <div className="form-actions">
        <button type="button" className="button is-quiet" disabled={pending} onClick={onCancel}>
          <span>Avbryt</span>
        </button>
        <button
          type="submit"
          className="button is-primary"
          disabled={!personReady || !proof.ready || pending}
        >
          <span>{pending ? "Skickar" : "Skicka förfrågan"}</span>
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------- the panel */

export function MembershipPanel() {
  const viewOnly = useViewOnly();
  const identity = usePortalIdentity();
  const factorQuery = useVerifiedFactor();
  const [asking, setAsking] = useState(false);
  const [sent, setSent] = useState(false);

  const tenantId = identity.data?.tenantId ?? null;
  const isContact = identity.data?.role === "client_authorised_contact";

  const people = useTenantPeople(tenantId);
  const requests = useMembershipRequests(tenantId, isContact);

  const factor: StepUpFactorState = {
    id: factorQuery.data ?? null,
    unknown: factorQuery.isError,
    pending: factorQuery.isLoading
  };

  if (identity.isLoading) {
    return (
      <Panel title="Personer med åtkomst" titleId="access-title" flush>
        <BoxState kind="loading" title="Hämtar ert kundkonto" compact />
      </Panel>
    );
  }

  // A person with no tenant has nothing to ask about. The panel says so instead of rendering an
  // empty list, which would read as "nobody at your company has access".
  if (!tenantId) {
    return null;
  }

  const rows = requests.data ?? [];

  return (
    <>
      <Panel
        title="Personer med åtkomst"
        titleId="access-title"
        flush
        testId="access-panel"
        foot={
          isContact ? (
            <>
              {sent ? (
                <p className="form-success k-set-message" role="status" data-testid="membership-sent">
                  Förfrågan är skickad. Säger Petrovic Systems ja får personen en inbjudan per mejl.
                </p>
              ) : null}
              {people.data && !viewOnly ? (
                <button
                  type="button"
                  className="button"
                  data-testid="membership-open"
                  onClick={() => {
                    setSent(false);
                    setAsking(true);
                  }}
                >
                  <UserPlus size={16} aria-hidden="true" />
                  <span>Begär en ändring</span>
                </button>
              ) : null}
            </>
          ) : (
            /*
              plan.md section 4: a client member "cannot end management or change access". The line
              says whose the change is rather than showing a control the server would refuse, which is
              the same choice `ManagementPanel` makes about a cancellation.
            */
            <span className="quiet k-set-foot-line" data-testid="membership-member-note">
              Ändringar av vilka som har åtkomst görs av er behöriga kontaktperson.
            </span>
          )
        }
      >
        {people.isLoading ? (
          <BoxState kind="loading" title="Hämtar vilka som har åtkomst" compact />
        ) : people.isError ? (
          <BoxState
            kind="error"
            title="Vi kunde inte hämta vilka som har åtkomst"
            body="Ingenting har ändrats. Försök igen."
            onRetry={() => void people.refetch()}
            compact
          />
        ) : (
          <AccessList people={people.data ?? []} />
        )}
      </Panel>

      {/* The contact's own requests are that person's; "Visa som kund" leaves them out (build round 41). */}
      {isContact && viewOnly ? <PersonalLeftOut /> : null}

      {isContact && rows.length > 0 ? (
        <Panel title="Era förfrågningar" titleId="access-requests-title" flush>
          <ul className="k-rows" data-testid="membership-request-list">
            {rows.map((request) => (
              <OutcomeLine key={request.invitation_id} request={request} />
            ))}
          </ul>
        </Panel>
      ) : null}

      {isContact && people.data && !viewOnly ? (
        <Sheet open={asking} onOpenChange={setAsking} title="Begär en ändring" testId="membership-sheet">
          <RequestForm
            tenantId={tenantId}
            people={people.data}
            factor={factor}
            onSent={() => {
              setSent(true);
              setAsking(false);
            }}
            onCancel={() => setAsking(false)}
          />
        </Sheet>
      ) : null}
    </>
  );
}
